import {IntegrationMode} from './integration-mode.model';
import {SchemaType} from './schema-type.model';
import {DataSourceType} from './data-source-type.model';
import {DataSourceFriendlyName} from '@app/views/admin/application-settings/models/data-source-type.model';

export class DataSourceConfig {
    public isAllowedRemoveRosterData: boolean;
    public isSaveDataSnapshots: boolean;
    public isDetectDuplicatesEnabled: boolean;
    public notValidConfigurationMessage: string;
    public significantUpdatePercent: number;
    public significantDeletePercent: number;
    public significantChangesMaxPercent: number;
    public mode: string;
    public isSupportedUsersWithMultipleRoles: boolean;

    constructor(
        public defaultSchemaName: DefaultSchemaName = <DefaultSchemaName>'',
        public friendlyName: string = '',
        public ownerId: string = '',
        public clientRef: string = '',
        public objectId: number = null,
        public dataSourceType: DataSourceType = null,
        public dname: string = '',
        public supportedModes: IntegrationMode[] = <IntegrationMode[]>[],
        public schemaTypeModelList: SchemaType[] = <SchemaType[]>[],
        public isValidConfiguration: boolean = true,
        public isSupportCustomFields: boolean = false,
        public isManuallyUpload: boolean = false,
        public isGradesCollectingEnabled: boolean = true,
        public sisIdPrefix: string = '',
        public orgGuid: string = '',
    ) {}
}

export const OneRoster10 = 'OneRoster10';
export const OneRoster11 = 'OneRoster11';
export const OneRoster12 = 'OneRoster12';
export const PreviewModeSignificantPercent = 0.00000001;

export const enum DefaultSchemaName {
    ONE_ROSTER_10 = 'OneRoster10',
    ONE_ROSTER_11 = 'OneRoster11',
    ONE_ROSTER_12 = 'OneRoster12',
    BASIC_ROSTER = 'Clever',
    RANK_ONE = 'RankOne',
    CUSTOM_ROSTER_DATA = 'CustomRosterData',
    AMPLIFY = 'Amplify',
    SCHOOL_TUBE = 'SchoolTube',
    SSO_APP = 'SSO_App'
}

export const DefaultSchemaList = [
    DefaultSchemaName.ONE_ROSTER_10,
    DefaultSchemaName.ONE_ROSTER_11,
    DefaultSchemaName.ONE_ROSTER_12,
    DefaultSchemaName.BASIC_ROSTER,
    DefaultSchemaName.RANK_ONE
];

export const DefaultSchemaListWithoutRankOne = [
    DefaultSchemaName.ONE_ROSTER_10,
    DefaultSchemaName.ONE_ROSTER_11,
    DefaultSchemaName.ONE_ROSTER_12,
    DefaultSchemaName.BASIC_ROSTER
];

export const RESTRICTED_DEFAULT_SCHEMA_LIST = [
    <string>DefaultSchemaName.ONE_ROSTER_12
];

export const DataSourceDefaultAppConfig = new Map<DataSourceType, Map<string, AppConfig>>([
    [
        DataSourceType.OneRoster,
        new Map<string, AppConfig>([
            [
                String(DataSourceFriendlyName['Aeries (OneRoster)']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/v2/oauth/token'}
                }
            ],
            [
                String(DataSourceFriendlyName.FACTS),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/oauth2/token'}
                }
            ],
            [
                String(DataSourceFriendlyName['Lumen Touch']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/ims/oneroster/v1p1/oauth/token'}
                }
            ],
            [
                String(DataSourceFriendlyName['Skyward (OneRoster)']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/token'}
                    // modify: {'authUrlSuffix': '/oauth/token'}
                }
            ],
            [
                String(DataSourceFriendlyName['Follett Aspen']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/oauth/rest/v2.0/auth'}
                }
            ],
            [
                String(DataSourceFriendlyName['eSchoolPlus']),
                <AppConfig>{
                    modify: {
                        'baseUrlSuffix': '/OneRoster',
                        'baseUrlOneRosterSuffix': false
                    }
                }
            ],
            [
                String(DataSourceFriendlyName.Sapphire),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/ims/oauth2/token'}
                }
            ],
            [
                String(DataSourceFriendlyName.Synergy),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/oauth/access_token'}
                }
            ],
            [
                String(DataSourceFriendlyName.ClassLink),
                <AppConfig>{
                    // defaults: {'oneRosterBaseUrl': 'https://classlink.com'}
                }
            ],
            [
                String(DataSourceFriendlyName['PowerSchool (OneRoster)']),
                <AppConfig>{
                    modify: {
                        'baseUrlSuffix': '/api',
                        'authUrlSuffix': '/ims/oneroster/v1p1/token'
                    }
                }
            ],
            [
                String(DataSourceFriendlyName['Edsembli']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/api/auth/token'}
                }
            ],
            [
                String(DataSourceFriendlyName['MichiganDataHub']),
                <AppConfig>{
                    modify: {'authUrlSuffix': '/ims/oneroster/v1p1/token'}
                }
            ]
        ])
    ],
    [
        DataSourceType.SFTP,
        new Map<string, AppConfig>([
            [
                String(DataSourceFriendlyName.Clever),
                <AppConfig>{
                    defaults: {
                        'host': 'sftp.clever.com',
                        'defaultSchemaName': DefaultSchemaName.BASIC_ROSTER
                    }
                }
            ]
        ])
    ]
]);

export const ArchiveOUSupportedDataSourceList = [DataSourceType.GSuite];

export interface AppConfig {
    defaults?: PropertiesConfig;
    modify?: PropertiesConfig;
}

export interface PropertiesConfig {
    [key: string]: string | boolean;
}

export class DataSourceConfigAuthenticatedUserProfile {
    displayName: string;
    familyName: string;
    givenName: string;
    primaryDomain: string;
    primaryEmail: string;
}

function getDataSourceAppConfig(dataSourceType: DataSourceType, appName: string): AppConfig | null {
    let result = null;
    if (DataSourceDefaultAppConfig.has(dataSourceType)) {
        const dataSourceAppConfigMap: Map<string, AppConfig> = DataSourceDefaultAppConfig.get(dataSourceType);
        if (dataSourceAppConfigMap.has(appName)) {
            result = dataSourceAppConfigMap.get(appName);
        }
    }
    return result;
}

export function setDataSourceDefaultConfigByAppName(dataSourceType: DataSourceType,
    appName: string,
    oldAppName: string,
    dataSourceConfig: DataSourceConfig): DataSourceConfig {
    // Clear old App default config
    const oldAppConfig: AppConfig = getDataSourceAppConfig(dataSourceType, oldAppName);
    if (oldAppConfig && oldAppConfig.defaults) {
        Object.keys(oldAppConfig.defaults)
            .forEach((paramName: string) => dataSourceConfig[paramName] = null);
    }

    const appConfig: AppConfig = getDataSourceAppConfig(dataSourceType, appName);
    if (appConfig && appConfig.defaults) {
        Object.keys(appConfig.defaults)
            .forEach((paramName: string) => dataSourceConfig[paramName] = appConfig.defaults[paramName]);
    }
    return dataSourceConfig;
}

export function getDataSourceModifyParam(dataSourceType: DataSourceType, appName: string, paramName: string): string | boolean | null {
    let result = null;
    const appConfig: AppConfig = getDataSourceAppConfig(dataSourceType, appName);
    if (appConfig && appConfig.modify && appConfig.modify.hasOwnProperty(paramName)) {
        result = appConfig.modify[paramName];
    }
    return result;
}

